import { useParams } from "react-router-dom";
import { useLocalStorage } from "react-use";
import { DEFAULT_DATASET_ID } from "datasets/utils/dataset";
import CeConfigResponse from "~/types/interfaces/ce-config";

const storageKey = "sc-ce-draft-config";

type DraftConfigSetter = (
  update:
    | Partial<CeConfigResponse>
    | ((prev: Partial<CeConfigResponse> | undefined) => Partial<CeConfigResponse> | undefined)
    | undefined
) => void;

const useDraftConfig = (): [Partial<CeConfigResponse> | undefined, DraftConfigSetter] => {
  const { datasetId = DEFAULT_DATASET_ID } = useParams();

  const [allDraftConfigs, setDraftConfigs] = useLocalStorage<
    Record<string, Partial<CeConfigResponse>>
  >(
    storageKey,
    {},
    {
      raw: false,
      serializer: (value) => JSON.stringify(value),
      deserializer: (value) => {
        try {
          const parsed = JSON.parse(value);

          // Check if the parsed value is an object but not a Record (has content property directly)
          // This indicates it's in the old format (single draft config)
          if (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed) &&
            (parsed.content || parsed.id || parsed.name)
          ) {
            // Convert old format to new format using DEFAULT_DATASET_ID as key
            return { [DEFAULT_DATASET_ID]: parsed };
          }

          // Return as is if it's already in the new format or empty
          return parsed;
        } catch (e) {
          return {};
        }
      },
    }
  );

  // Get the draft config for the current datasetId
  const draftConfig = allDraftConfigs?.[datasetId];

  const updateDraftConfig: DraftConfigSetter = (update) => {
    if (update === undefined) {
      // Handle setting to undefined (clearing the draft)
      setDraftConfigs((prev) => {
        const newDrafts = { ...prev };
        delete newDrafts[datasetId];
        return newDrafts;
      });
    } else if (typeof update === "function") {
      // Handle functional updates
      setDraftConfigs((prev) => {
        const currentDraft = prev?.[datasetId];
        const newDraft = update(currentDraft);

        if (newDraft === undefined) {
          // If the function returns undefined, remove this draft
          const newDrafts = { ...prev };
          delete newDrafts[datasetId];
          return newDrafts;
        }

        return { ...prev, [datasetId]: newDraft };
      });
    } else {
      // Handle direct value updates
      setDraftConfigs((prev) => ({ ...prev, [datasetId]: update }));
    }
  };

  return [draftConfig, updateDraftConfig];
};

export default useDraftConfig;
